// La barra de entrada del chat: adjuntos enganchados, textarea que crece, el
// menú "+" y el botón de enviar/detener.
//
// Compartida entre el panel lateral y la pantalla dedicada — no hay una
// segunda copia. El look sale de `ChatPanelV2` (misma tarjeta blanca flotando
// con sombra, mismo textarea sin borde, mismos botones de 36px).

import { useEffect, useRef, useState } from 'react';
import { ArrowUp, FileText, Loader2, Plus, Square, X } from 'lucide-react';
import {
  ACEPTA_ADJUNTOS,
  formatearPeso,
  lugarLibreAdjuntos,
  mensajeLimiteAdjuntos,
} from '../adjuntosChatAtlas';
import { MenuMas } from './MenuMas';
import { prepararAdjunto, urlDeAdjunto } from './prepararAdjunto';
import type { AdjuntoChat, InterruptoresChat } from '../tipos';

interface Props {
  valor: string;
  onCambiarValor: (texto: string) => void;
  onEnviar: (texto: string) => void;
  streaming: boolean;
  onDetener: () => void;
  interruptores: InterruptoresChat;
  onCambiarInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  adjuntos: AdjuntoChat[];
  onCambiarAdjuntos: React.Dispatch<React.SetStateAction<AdjuntoChat[]>>;
  /** Foco automático al montar (el panel lo pide; la pantalla también). */
  autoFoco?: boolean;
  placeholder?: string;
}

export const BarraEntradaChat: React.FC<Props> = ({
  valor,
  onCambiarValor,
  onEnviar,
  streaming,
  onDetener,
  interruptores,
  onCambiarInterruptor,
  adjuntos,
  onCambiarAdjuntos,
  autoFoco,
  placeholder = 'Preguntále lo que quieras a Atlas…',
}) => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [cargandoAdjunto, setCargandoAdjunto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const archivoRef = useRef<HTMLInputElement | null>(null);
  const masRef = useRef<HTMLButtonElement | null>(null);

  // Auto-resize del textarea, con tope: pasado el tope scrollea adentro en vez
  // de comerse la conversación.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [valor]);

  useEffect(() => {
    if (autoFoco) textareaRef.current?.focus();
  }, [autoFoco]);

  const hayTexto = valor.trim().length > 0;
  const puedeEnviar = (hayTexto || adjuntos.length > 0) && !streaming && !cargandoAdjunto;

  const enviar = () => {
    if (!puedeEnviar) return;
    onEnviar(valor);
    setAviso(null);
  };

  const elegirArchivos = () => {
    setAviso(null);
    archivoRef.current?.click();
  };

  const alElegir = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const elegidos = Array.from(ev.target.files ?? []);
    // El input se limpia YA: sin esto, volver a elegir el mismo archivo no
    // dispara `change` (el valor no cambió) y el usuario cree que se rompió.
    ev.target.value = '';
    if (elegidos.length === 0) return;

    const libre = lugarLibreAdjuntos(adjuntos.length);
    if (libre === 0) {
      setAviso(mensajeLimiteAdjuntos());
      return;
    }
    const aProcesar = elegidos.slice(0, libre);
    const sobraron = elegidos.length > libre;

    setCargandoAdjunto(true);
    const nuevos: AdjuntoChat[] = [];
    let fallo: string | null = null;
    for (const archivo of aProcesar) {
      try {
        nuevos.push(await prepararAdjunto(archivo));
      } catch (err) {
        // Se guarda el PRIMER motivo y se sigue con el resto: que un archivo
        // no sirva no tiene por qué tirar abajo a los otros dos.
        fallo =
          fallo ?? (err instanceof Error ? err.message : 'No pude leer ese archivo.');
      }
    }
    setCargandoAdjunto(false);
    if (nuevos.length > 0) onCambiarAdjuntos((prev) => [...prev, ...nuevos]);
    setAviso(fallo ?? (sobraron ? mensajeLimiteAdjuntos() : null));
  };

  const quitarAdjunto = (id: string) => {
    onCambiarAdjuntos((prev) => prev.filter((a) => a.id !== id));
    setAviso(null);
  };

  return (
    <div className="relative">
      <input
        ref={archivoRef}
        type="file"
        multiple
        accept={ACEPTA_ADJUNTOS}
        onChange={alElegir}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Radios concéntricos: tarjeta 16 = textarea/controles 12 + 4. */}
      <div className="rounded-2xl bg-white p-1 shadow-[0_2px_14px_-6px_rgba(2,6,43,0.18),0_1px_3px_rgba(2,6,43,0.06)] ring-1 ring-[#02062B]/[0.06] transition-shadow duration-200 focus-within:ring-[#1644EB]/[0.28]">
        {(adjuntos.length > 0 || cargandoAdjunto) && (
          <div className="flex flex-wrap items-center gap-2 px-2.5 pt-2.5">
            {adjuntos.map((a) => (
              <span
                key={a.id}
                className="group relative inline-flex items-center gap-2 rounded-xl border border-[#02062B]/[0.08] bg-[#F0F2F7] py-1.5 px-2.5"
              >
                {a.clase === 'imagen' ? (
                  // `data:` URL en memoria: sin red, sin caché que compartir.
                  // Ver la nota de `BurbujaMensaje` sobre CachedImg.
                  <img
                    src={urlDeAdjunto(a)}
                    alt=""
                    className="h-7 w-7 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <FileText
                    className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]"
                    aria-hidden="true"
                  />
                )}
                <span className="min-w-0 max-w-[140px]">
                  <span className="block truncate text-[12.5px] font-medium text-brand-black">
                    {a.nombre}
                  </span>
                  <span className="block text-[11px] text-[#02062B]/[0.5]">
                    {formatearPeso(a.bytes)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => quitarAdjunto(a.id)}
                  aria-label={`Quitar ${a.nombre}`}
                  className="ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.45] transition-colors hover:bg-[#02062B]/[0.08] hover:text-brand-black"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            {cargandoAdjunto && (
              <span className="inline-flex items-center gap-2 rounded-xl bg-[#F0F2F7] py-2 px-3 text-[12.5px] font-medium text-[#02062B]/[0.55]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Preparando…
              </span>
            )}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={valor}
          onChange={(e) => onCambiarValor(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
          placeholder={placeholder}
          rows={1}
          className="w-full resize-none bg-transparent px-3 pt-4 text-[15px] leading-snug text-[#02062B]/[0.9] outline-none placeholder:text-[#02062B]/[0.38]"
        />

        <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-2">
          <button
            ref={masRef}
            type="button"
            aria-label="Más opciones"
            aria-haspopup="menu"
            aria-expanded={menuAbierto}
            onClick={() => setMenuAbierto((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                setMenuAbierto(true);
              }
            }}
            className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              menuAbierto
                ? 'bg-[#02062B]/[0.09] text-brand-black'
                : 'bg-[#F0F2F7] text-[#02062B]/[0.7] hover:bg-[#02062B]/[0.09]'
            }`}
          >
            <Plus className="h-4 w-4" strokeWidth={2.25} />
            {/* Investigación prendida es lo único de este menú que sale plata
                de verdad (scrapers). Con el menú cerrado no habría forma de
                saberlo: el punto es esa señal. Decorativo — el estado real lo
                dice `aria-checked` dentro del menú. */}
            {interruptores.investigacion && (
              <span
                aria-hidden="true"
                className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand-primary"
              />
            )}
          </button>

          {streaming ? (
            <button
              type="button"
              onClick={onDetener}
              aria-label="Detener"
              // Fondo SÓLIDO (`bg-brand-black`, sin `/85`): las clases
              // `brand-*` salen de un `var()` y con modificador de opacidad
              // Tailwind genera CSS inválido — el botón quedaba invisible.
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-sm transition-opacity duration-200 hover:opacity-90"
            >
              <Square className="h-3 w-3 fill-current" />
            </button>
          ) : (
            <button
              type="button"
              onClick={enviar}
              disabled={!puedeEnviar}
              aria-label="Enviar"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-colors duration-200 ${
                puedeEnviar
                  ? 'bg-brand-primary text-white hover:bg-brand-primary-hover'
                  : 'cursor-not-allowed bg-[#F0F2F7] text-[#02062B]/[0.3]'
              }`}
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {aviso && (
        <p role="status" className="mt-2 px-1 text-[12.5px] text-[#C33B36]">
          {aviso}
        </p>
      )}

      <MenuMas
        abierto={menuAbierto}
        onCerrar={() => setMenuAbierto(false)}
        interruptores={interruptores}
        onCambiarInterruptor={onCambiarInterruptor}
        onAdjuntar={elegirArchivos}
        disparadorRef={masRef}
      />
    </div>
  );
};

export default BarraEntradaChat;
